// src/components/Dropdown.js
import React, { useEffect, useMemo, useRef, useState } from 'react';

export default function Dropdown({ value, onChange, options, placeholder='All', className='', hideAll=false }) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);

  const allItems = useMemo(()=> options.map(o =>
    typeof o === 'object' ? o : { value: o, label: String(o ?? '—') }
  ), [options]);

  const filteredItems = useMemo(() =>
    allItems.filter(item =>
      item.label.toLowerCase().includes(searchTerm.toLowerCase())
    ), [allItems, searchTerm]);

  const current = allItems.find(i => i.value === value) || null;

  useEffect(()=>{
    const onDoc = (e)=>{
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return ()=> document.removeEventListener('mousedown', onDoc);
  },[]);

  useEffect(() => {
    if (open) {
      setSearchTerm('');
      setTimeout(() => searchRef.current?.focus(), 100);
    }
  }, [open]);

  const handleKey = (e)=>{
    if (e.target.tagName === 'INPUT') return;
    if (!open && (e.key === 'Enter' || e.key === ' ')) { setOpen(true); e.preventDefault(); return; }
    if (!open) return;
    const idx = filteredItems.findIndex(i => i.value === (current?.value));
    if (e.key === 'ArrowDown'){ const n = filteredItems[Math.min(idx+1, filteredItems.length-1)]; onChange(n?.value ?? ''); e.preventDefault(); }
    if (e.key === 'ArrowUp'){ const p = filteredItems[Math.max(idx-1, 0)]; onChange(p?.value ?? ''); e.preventDefault(); }
    if (e.key === 'Enter' || e.key === 'Escape'){ setOpen(false); e.preventDefault(); }
  };

  return (
    <div ref={rootRef} className={`dd ${open ? 'open' : ''} ${className}`} tabIndex={0} onKeyDown={handleKey}>
      <button className="dd-head" type="button" onClick={()=>setOpen(o=>!o)}>
        <span className={`dd-value ${current ? '' : 'muted'}`}>{current?.label ?? placeholder}</span>
        <span className="dd-caret" />
      </button>
      {open && (
        <div className="dd-menu" ref={listRef}>
          <div className="dd-search">
            <input
              ref={searchRef}
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          {!hideAll && <div className={`dd-item ${current ? '' : 'selected'}`} onClick={()=>{ onChange(''); setOpen(false); }}>{placeholder}</div>}
          {filteredItems.map((i,ix)=>(
            <div key={ix}
                 className={`dd-item ${current && i.value === current.value ? 'selected' : ''}`}
                 onClick={()=>{ onChange(i.value); setOpen(false); }}>
              {i.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}